import React, { useState, useMemo } from 'react'
import { makeStyles } from '@mui/styles'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { FineUploaderBasic as Uploader } from 'fine-uploader'
import moment from 'moment'
import { user } from 'ampersand-app'
import PropTypes from 'prop-types'
import lang from '@/lang'
import config from '@/config'
import todoConfig from '@/modules/todo/config'
import { useSelector, useDispatch } from 'react-redux'
import PerfectScrollbar from 'react-perfect-scrollbar'

import { TextareaInput, DateInput, TextInput } from '@/modules/base/components/inputs/index'
import { SupplierSelect } from '@/modules/supplier/components/supplier-select/supplier-select'
import { CustomerSelect } from '@/modules/customer/components/customer-select/customer-select'
import { UserSelect } from '@/modules/user/components/user-select/user-select'
import { CollaborationSelect } from '@/modules/todo/components/todo-form/collaboration-select'
import { IntervalSelect } from '@/modules/todo/components/_helpers/interval-select'
import { TypeSelect } from '@/modules/todo/components/todo-form/todo-type-select/todo-type-select'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { ButtonIconSquare, SquareIconButton } from '@/modules/base/components/buttons'
import { errorCodes, SUBSCRIPTION_MODULES, TODO_COLLABORATION_ID, experimentalFeatures, FILE_LIST_FILTER } from '@/modules/base/util/constants'
import { checkModuleAccess } from '@/modules'
import customDecodeUri from '@/utils/customDecodeUri'
import TodoTypes from '@/enums/todo-types.enum'

import { getUploader } from '@/modules/file/uploader'
import { toast } from '@/modules/base/components/toast/toastConfig'
import TodoService from '@/modules/todo/todoService'

import IconMagnifier from '@assets/images/icons/search.svg'
import IconDelete from '@assets/images/icons/close.svg'
import IconFileUpload from '@assets/images/icons/upload.svg'
import IconFileContainer from '@assets/images/icons/link.svg'
import IconPlus from '@assets/images/icons/plus.svg'
import IconCheck from '@assets/images/icons/check.svg'
import TodoIntervalEnum from '@/enums/todoInterval.enum'
import handleUploadError from '@/modules/file/_helpers/handleUploadError'
import validateFileName from '@/utils/validateFileName'
import TodoFormFileUpload from '@/modules/todo/components/todo-form/todo-form-file-upload'
import SignatureWizard from '@/modules/signature/components/wizard/signature-wizard'
import TodoFilePreview from '@/modules/todo/components/todo-files/todo-file-preview'
import { executeUpload } from '@/redux/ducks/document/uploadHelpers'
import { useAppSelector } from '@/redux/store'
import { previewMimeTypes } from '@/modules/document/components/document-drawer/document-drawer'
import { refreshList } from '@/redux/ducks/lists/requests/todoList'
import { thumbnailMimeTypes } from '@/modules/receipt/preview/item/receipt-previewer-item'
import { Backdrop, CircularProgress } from '@mui/material'
import { closeUploadDrawer, removeUploadFromDrawer } from '@/redux/ducks/document/upload'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import IconInfo from '@assets/images/icons/info.svg'
import TextEllipsis from '@/modules/base/components/texts/text-ellipsis'

const { dateFormat: { api: apiDateFormat } } = config

const useStyles = makeStyles(theme => ({
  wrapper: {
    height: '100%',
    display: 'flex',
    position: 'relative',
  },
  root: {
    display: 'flex',
    overflow: 'auto',
    height: '100%',
    flex: '1 1 0',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    width: '100%',
  },
  footer: {
    padding: '0px 30px',
    background: theme.backgroundColor,
  },
  signatureWrapper: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },
  signatureTypeSelection: {
    padding: '35px 30px 0px 30px',
  },
  signatureContent: {
    padding: 30,
    borderLeft: `1px solid ${theme.borderColors.default}`,
  },
  signatureFooter: {
    borderLeft: `1px solid ${theme.borderColors.default}`,
  },
  formContainer: {
    gridArea: 'form',
    display: 'flex',
    flexDirection: 'column',
    margin: 0,
    height: '100%',
    overflow: 'auto',
  },
  form: ({ collapse }) => ({
    padding: '30px 0px',
    '& > section': {
      padding: '10px 0',
    },
  }),
  dates: ({ collapse, fullForm }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    width: fullForm ? '100%' : collapse ? 'calc(50% - 10px)' : '100%',
    '& > div': {
      width: 'calc(50% - 10px)',
    },
  }),
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '5px 10px',
    '& > svg': {
      marginRight: 5,
    },
  },
  fileButtons: {
    marginLeft: 'auto',
    display: 'flex',
    '& > *': {
      padding: 2,
    },
  },
  buttons: {
    height: 85,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexShrink: 0,
  },
  collapseRow: ({ collapse }) => ({
    display: 'flex',
    flexDirection: collapse ? 'row' : 'column',
    justifyContent: 'space-between',
  }),
  intervals: ({ collapse }) => {
    if (collapse) {
      return {
        border: '0 none',
        margin: 0,
        width: 'calc(25% - 10px)',
        padding: 0,
      }
    } else {
      return {
        paddingTop: 0,
        marginTop: 0,
        width: '100%',
      }
    }
  },
  customers: ({ collapse }) => {
    if (collapse) {
      return {
        border: '0 none',
        margin: 0,
        width: 'calc(50% - 10px)',
        padding: 0,
      }
    }
  },
  suppliers: ({ collapse }) => {
    if (collapse) {
      return {
        border: '0 none',
        margin: 0,
        width: 'calc(50% - 10px)',
        padding: 0,
      }
    } else {
      return {
        borderTop: `1px solid ${theme.borderColor}`,
        paddingTop: 20,
        marginTop: 20,
        width: '100%',
      }
    }
  },
  documents: {
    display: 'inherit',
  },
  textarea: {
    '& textarea': {
      height: 64,
      padding: '10px 8px',
    },
  },
  documentLabel: {
    paddingBottom: 10,
    fontSize: theme.fontSizes.small,
  },
  loaderBackdrop: {
    background: 'rgba(255, 255, 255, 0.7)',
    zIndex: 1000,
    position: 'absolute',
  },
  tooltipContainer: {
    display: 'flex',
  },
  tooltipText: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: theme.fontSizes.smaller,
    margin: 0,
    color: theme.lighterBlue,
    fontStyle: 'italic',
    cursor: 'pointer',
    marginTop: 10,
    zIndex: 100000,
  },
  fileName: ({ inline }) => ({
    maxWidth: inline ? '100%' : 350,
    [theme.breakpoints.down('lg')]: {
      maxWidth: '100%',
    },
  }),
}))

const TodoForm = (props) => {
  const {
    todo,
    callbackAfterSave,
    collapse = false,
    customers,
    fullForm,
    withTypeSelect = false,
    withoutSignatureOption,
    withClientSelect = false,
    tenantCollaborationId = null,
    tenantCollaborationName = null,
    document,
    inline = false,
  } = props
  const [isFileContainerOpen, setIsFileContainerOpen] = useState(false)
  const [filesToUpload, setFilesToUpload] = useState(document ? [{ file: document, source: 'container' }] : [])
  const [previewFile, setPreviewFile] = useState({ visible: false, file: {} })
  const [responsibleUserCount, setResponsibleUserCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const { companyName, senderEmail } = useSelector(state => state.user.info.activeTenant)
  const fileToSign = useSelector(state => state.signature.edit.file)
  const dispatch = useDispatch()
  const subscriptions = useSelector(state => state.subscription.details.subscriptions)
  const hasActiveSubscription = subscriptions.some(subscription => (
    (subscription.end === null || moment(subscription.end).isAfter(moment()))
    && !subscription.isLocked
  ))

  const validationSchema = Yup.object({
    title: Yup.string()
      .required(lang.emptyRequiredFieldErrorMessage)
      .test('maxChars', lang.todo.maxLengthErrorMessage, value => {
        if (!value) return true
        return value.length <= 100
      }),
    responsibleUser: Yup.object({
      value: Yup.string().required(lang.emptyRequiredFieldErrorMessage),
      label: Yup.string(),
    }),
  })

  const isEdit = Boolean(todo)
  const hasClientModuleAccess = checkModuleAccess(SUBSCRIPTION_MODULES.CLIENTS)
  const shouldRenderClientSelect = hasClientModuleAccess && withClientSelect
  const classes = useStyles({ collapse, fullForm, inline })

  const buildName = (object) => {
    return object?.name
      ? object.name
      : ((object?.firstName || object?.lastName)
        ? `${object?.firstName} ${object?.lastName}`
        : object?.email || object?.invitationEmail)
  }

  let presetCustomers = []
  if (isEdit) {
    presetCustomers = todo.customers?.map(customer => ({ value: customer.id, label: customer.name })) || []
  } else {
    presetCustomers = customers?.map(customer => ({ value: customer.id, label: buildName(customer) })) || []
  }

  const onSelectFromFileContainer = (file) => {
    const fileToAdd = { file, source: file?.isUpload ? 'upload' : 'container' }
    setIsFileContainerOpen(false)
    setFilesToUpload((filesToUpload) => {
      const newList = [
        ...filesToUpload.filter(fileEntry => fileEntry.file.id !== file.id),
        fileToAdd,
      ]
      return newList
    })
  }

  const onPreviewFile = (file, previewControlHandlers) => {
    if (previewFile.visible === false && isFileContainerOpen) {
      setPreviewFile({ file, visible: true, previewControlHandlers })
    }
  }

  const onFileSubmitted = (id, name) => {
    const file = uploader.methods.getFile(id)
    const reader = new FileReader()
    reader.onabort = (error) => {
      toast.showToast({ type: 'error', message: error })
    }
    reader.onerror = (error) => {
      toast.showToast({ type: 'error', message: error })
    }
    reader.onload = () => {
      const data = reader.result
      const fileToAdd = { data, file, source: 'upload' }
      setFilesToUpload(filesToUpload => {
        const newList = [...filesToUpload, fileToAdd]
        return newList
      })
    }
    reader.readAsDataURL(file)
  }

  const onFileUploadError = (id, name, errorReason, xhr) =>
    handleUploadError(xhr, errorReason)

  const uploader = getUploader({
    callbacks: {
      onSubmitted: onFileSubmitted,
      onError: onFileUploadError,
      onValidate: (file) => validateFileName(file),
    },
  })

  const uploadFile = (file) => {
    return new Promise((resolve, reject) => {
      const uploader = new Uploader({
        autoUpload: false,
        multiple: false,
        request: {
          endpoint: todoConfig.uploadUrl,
          customHeaders: { authorization: `Bearer ${user?.token}` },
          inputName: 'file',
        },
        callbacks: {
          onError: (id, name, reason, xhr) => reject(reason),
          onComplete: (id, name, response, xhr) => resolve({ body: response }),
          onValidate: (file) => validateFileName(file),
        },
      })
      uploader.setParams({ filename: file.name })
      uploader.addBlobs([{ name: file.name, blob: file }])
      uploader.uploadStoredFiles()
    })
  }

  const createSignatureTodo = async(data) => {
    try {
      await TodoService.createSignatureTodo(data)
      toast.showToast({ message: lang.todo.saveSuccessMessage(TodoIntervalEnum.SINGLE) })
    } catch (error) {
      let errorMessage = lang.todo.createErrorMessage
      if (error?.meta?.encryption?.[0].code === errorCodes.NOT_ALLOWED) {
        errorMessage = lang.fileUploaderSignatureEncryptionError
      }

      toast.showToast({ type: 'error', message: errorMessage })
    }
  }

  const handleSignatureWizardFinished = async(signatureData, formik, formikValues) => {
    const todo = await createSignatureTodo(signatureData)
    dispatch(refreshList())
    callbackAfterSave && callbackAfterSave(todo, formik, { reset: () => setFilesToUpload([]) }, responsibleUserCount, formikValues)
  }

  const useOldUpload = !user?.experimentalFeatures?.includes(experimentalFeatures.dmsDirectUpload)
  const drawer = useAppSelector(state => state.document.upload.drawer)
  const uploadItems = Array.isArray(drawer?.files) ? drawer.files : []

  const onSubmit = async(values, formikBag) => {
    setIsLoading(true)
    let fileIds = []
    let todoDocumentIds = []

    const isInterval = [
      TodoIntervalEnum.DAILY,
      TodoIntervalEnum.WEEKLY,
      TodoIntervalEnum.MONTHLY,
      TodoIntervalEnum.QUARTERLY,
      TodoIntervalEnum.YEARLY,
    ].includes(values?.interval)

    const getTodoValues = (values, initialValues, fileIds, todoDocumentIds) => {
      return {
        title: values.title,
        content: values.content,
        dueFromDate: isInterval ? null : moment(values.dueFromDate).startOf('day').format(apiDateFormat),
        dueToDate: moment(values.dueToDate).endOf('day').format(apiDateFormat),
        userIds: values.responsibleUser ? [values.responsibleUser.value] : [],
        customerIds: values.customers.map(customer => customer.value),
        supplierIds: values.suppliers.map(supplier => supplier.value),
        tenantIds: values?.collaborators?.value ? [values.collaborators.value] : [],
        interval: values.interval,
        origin: initialValues.origin,
        type: values.type,
        tenantCollaborationId: values?.tenantCollaborationId?.value !== TODO_COLLABORATION_ID.INTERNAL ? values?.tenantCollaborationId?.value : null,
        ...(Array.isArray(fileIds) && { fileIds }),
        ...(Array.isArray(todoDocumentIds) && { todoDocumentIds }),
      }
    }

    if (!isEdit) {
      if (useOldUpload) {
        const requests = filesToUpload
          .filter(fileEntry => fileEntry.source === 'upload')
          .map(fileData => uploadFile(fileData.file))

        const results = await Promise.allSettled(requests)

        const rejected = results.filter((result) => result.status === 'rejected')
        if (rejected.length > 0) {
          toast.showToast({ type: 'error', message: lang.todo.createTaskErrorMessage })
          setIsLoading(false)
          return
        }

        const fulfilled = results.filter((result) => result.status === 'fulfilled')
        todoDocumentIds = fulfilled.map(({ value: { body: { data } } }) => data[0].id)
        fileIds = filesToUpload.filter(fileEntry => fileEntry.source === 'container').map(fileEntry => fileEntry.file.id)
      } else {
        const fileIdsFromContainer = filesToUpload.filter(fileEntry => fileEntry.source === 'container').map(fileEntry => fileEntry.file.id).filter(id => !id.startsWith('batch')).filter(Boolean)
        fileIds = fileIdsFromContainer

        try {
          const newTodo = await TodoService.create(getTodoValues(values, initialValues, fileIds, todoDocumentIds))
          setFilesToUpload([])
          dispatch(closeUploadDrawer())
          dispatch(refreshList())

          if (callbackAfterSave) {
            callbackAfterSave(newTodo, formikBag, { reset: () => setFilesToUpload([]) }, responsibleUserCount, values)
          }

          const filesToUploadFromDrawer = uploadItems.filter(item => !fileIdsFromContainer.includes(item.id)).map(item => item.file)

          if (filesToUploadFromDrawer.length > 0) {
            executeUpload({
              params: {
                syncToMbk: false,
                metaData: {},
                recordDate: moment().format(apiDateFormat),
                tags: [],
                notes: values.content || '',
                folderId: null,
              },
              files: filesToUploadFromDrawer,
              scan: false,
              closeOverlayAfterComplete: true,
              source: 'todo',
              todoId: newTodo.id,
              onFileUploaded: (fileId) => {
                TodoService.link(
                  newTodo.id,
                  [TodoService.createRelationItem(fileId, TodoService.TYPE_FILE)],
                )
              },
            }).catch(error => {
              console.error('Background upload failed:', error)
              toast.showToast({ type: 'warning', message: lang.todo.uploadFileErrorMessage })
            })
          }

          toast.showToast({ message: lang.todo.saveSuccessMessage(values.interval) })
          setIsLoading(false)
          return
        } catch (e) {
          toast.showToast({ type: 'error', message: lang.todo.createErrorMessage })
          setIsLoading(false)
          return
        }
      }
    } else {
      fileIds = todo.files.map(file => file.id)
      todoDocumentIds = todo.documents.map(document => document.id)
    }

    try {
      let newTodo
      if (isEdit) {
        newTodo = await TodoService.edit({
          ...getTodoValues(values, initialValues, fileIds, todoDocumentIds),
          id: todo.id,
        })
        toast.showToast({ message: lang.todo.updateSuccessMessage(values.interval) })
        callbackAfterSave?.(newTodo)
      } else {
        newTodo = await TodoService.create(getTodoValues(values, initialValues, fileIds, todoDocumentIds))
        setFilesToUpload([])
        !useOldUpload && dispatch(closeUploadDrawer())
        dispatch(refreshList())

        // For old upload system, show notification immediately since uploads are already handled
        if (useOldUpload) {
          toast.showToast({ message: lang.todo.saveSuccessMessage(values.interval) })
        }
      }
      if (callbackAfterSave && !isEdit) {
        callbackAfterSave(newTodo, formikBag, { reset: () => setFilesToUpload([]) }, responsibleUserCount, values)
      }
    } catch (e) {
      toast.showToast({ type: 'error', message: lang.todo[`${isEdit ? 'upd' : 'cre'}ateErrorMessage`] })
    } finally {
      setIsLoading(false)
    }
  }

  const removeFileFromList = (index) => {
    !useOldUpload && dispatch(removeUploadFromDrawer(index))
    setFilesToUpload(filesToUpload => {
      const newFilesToUpload = filesToUpload.filter((_, i) => i !== index)
      return [...newFilesToUpload]
    })
  }

  const showFilePreview = (index) => {
    const { file, data, source } = filesToUpload[index]
    if (previewFile?.file.name === file.name && previewFile.visible) {
      return onClosePreview()
    }
    if (source === 'upload') {
      const { url, name, size, type, lastModifiedDate } = file
      const previewFile = {
        url: data ?? url,
        name,
        size,
        mimetype: type,
        type: 'document',
        createdAt: lastModifiedDate,
        updatedAt: new Date(),
      }
      setPreviewFile({ file: previewFile, visible: true })
    } else {
      setPreviewFile({ file, visible: true })
    }
  }

  const onClosePreview = () => {
    const { previewControlHandlers } = previewFile
    if (previewControlHandlers) {
      previewControlHandlers.handleClosePreview()
    }
    setPreviewFile({ visible: false, file: {}, previewControlHandlers: undefined })
  }

  const onRemoveFile = (i) => {
    if (previewFile?.file.name === filesToUpload[i].file.name) {
      onClosePreview()
    }
    removeFileFromList(i)
  }

  const fileList = useMemo(() => (
    <section>
      {filesToUpload.map(({ file, source }, index) => {
        const disablePreview = thumbnailMimeTypes.includes(file?.mimetype) && !file.minioId
        return (
          <div key={index} className={classes.fileRow}>
            <SVGIcon width='20px' height='20px' svg={source === 'upload' ? IconFileUpload : IconFileContainer} />
            <TextEllipsis title={customDecodeUri(file.name)} className={classes.fileName}>
              {customDecodeUri(file.name)}
            </TextEllipsis>
            <div className={classes.fileButtons}>
              {!document
              && (
                <>
                  {previewMimeTypes.includes(file.mimetype || file.type) && !inline && (
                    <SquareIconButton
                      tooltipIgnoreDisabled
                      size='medium'
                      icon={IconMagnifier}
                      disabled={disablePreview}
                      tooltip={disablePreview ? lang.todo.form.previewDisabled : null}
                      onClick={() => showFilePreview(index)}
                      variant='white'
                    />
                  )}
                  <SquareIconButton
                    size='medium'
                    icon={IconDelete}
                    onClick={() => onRemoveFile(index)}
                    variant='white'
                  />
                </>
              )}
            </div>
          </div>
        )
      },
      )}
    </section>
  ), [filesToUpload, previewFile])

  const initialTenantCollaborationId = () => {
    if (!shouldRenderClientSelect) return null
    if (tenantCollaborationId) return { value: tenantCollaborationId, label: tenantCollaborationName }
    if (todo?.tenantCollaboration.id) return { value: todo?.tenantCollaboration?.id, label: todo?.tenantCollaboration?.name }
    return { value: TODO_COLLABORATION_ID.INTERNAL, label: companyName ?? senderEmail ?? lang.selectInput.user.ownCompany }
  }

  const initialValues = {
    title: todo?.title || '',
    content: todo?.content || '',
    dueFromDate: todo?.dueFromDate || moment().format(apiDateFormat),
    dueToDate: todo?.dueToDate || moment().format(apiDateFormat),
    interval: todo?.todoIntervalTemplate?.interval || TodoIntervalEnum.SINGLE,
    responsibleUser: todo?.users?.[0] ? { value: todo.users[0].id, label: buildName(todo.users[0]) } : '',
    customers: presetCustomers,
    suppliers: todo?.suppliers?.map(supplier => ({ value: supplier.id, label: supplier.name })) || [],
    type: TodoTypes.PROCESS,
    tenantCollaborationId: initialTenantCollaborationId(),
  }

  const minDueFromDate = isEdit && todo?.dueFromDate ? moment(todo.dueFromDate).toDate() : moment().toDate()

  return (
    <div className={classes.wrapper}>
      {isLoading && (
        <Backdrop open={isLoading} classes={{ root: classes.loaderBackdrop }}>
          <CircularProgress color='primary' />
        </Backdrop>
      )}
      {previewFile.visible ? <TodoFilePreview file={previewFile.file} /> : null}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({ values, setFieldValue, errors, touched, resetForm, isSubmitting, submitForm }) => {
          const hasSupplierModuleAccess = checkModuleAccess(SUBSCRIPTION_MODULES.SUPPLIERS)
          const hasCustomerModuleAccess = checkModuleAccess(SUBSCRIPTION_MODULES.CUSTOMERS)
          const tenantCollaborationId = values?.tenantCollaborationId?.value
          const hasClientActiveSubscription = tenantCollaborationId === TODO_COLLABORATION_ID.INTERNAL || values?.tenantCollaborationId?.hasActiveSubscription

          const typeSelection = (
            <TypeSelect
              notAsync
              useFormik
              name="type"
              value={values.type}
              withoutSignatureOption={withoutSignatureOption}
              document={document}
              label={lang.todo.type}
              onChange={(value) => {
                const { type: previousType } = values
                // remove files and reset form when switching between signature and any other type
                // signature has specific upload requirements (e.g. only pdf) and fields
                if (value === TodoTypes.SIGNATURE || previousType === TodoTypes.SIGNATURE) {
                  onClosePreview()
                  filesToUpload.map((file, index) => removeFileFromList(index))
                  resetForm()
                }
                setFieldValue('type', value)
              }}
            />
          )

          if (values.type === TodoTypes.SIGNATURE) {
            return (
              <div className={classes.signatureWrapper}>
                {!fileToSign && <div className={classes.signatureTypeSelection}>{typeSelection}</div>}
                <SignatureWizard
                  tenantCollaborationId={tenantCollaborationId}
                  styles={{
                    content: classes.signatureContent,
                    footer: classes.signatureFooter,
                  }}
                  onFinish={(signatureData) => {
                    handleSignatureWizardFinished(signatureData, { resetForm }, values)
                  }}
                  document={document}
                  filter={FILE_LIST_FILTER.NO_SYSTEM_DOCUMENTS}
                />
              </div>
            )
          }

          return (
            <div className={classes.root}>
              <div className={classes.content}>
                <Form className={classes.formContainer}>
                  <PerfectScrollbar options={{ suppressScrollX: true, scrollYMarginOffset: 10 }}>
                    <div className={classes.form}>
                      {withTypeSelect && (<section>{typeSelection}</section>)}
                      <section>
                        <TextInput
                          name="title"
                          label={!collapse ? lang.todo.form.title : null}
                          value={values.title}
                          className={classes.textarea}
                          placeholder={lang.todo.titlePlaceholder}
                          useFormik
                        />
                      </section>
                      <section>
                        <TextareaInput
                          name="content"
                          label={!collapse ? lang.todo.form.content : null}
                          value={values.content}
                          className={classes.textarea}
                          placeholder={lang.todo.placeholder}
                          useFormik
                        />
                      </section>

                      <section className={classes.collapseRow}>
                        <div className={classes.dates}>
                          {
                            TodoIntervalEnum.SINGLE === values?.interval && (
                              <DateInput
                                label={lang.todo.form.dueDate}
                                name="dueFromDate"
                                useFormik
                                minDate={minDueFromDate}
                              />
                            )
                          }
                          <DateInput
                            label={lang.todo.form.deadline}
                            name="dueToDate"
                            useFormik
                            minDate={(values.dueFromDate && values?.interval === TodoIntervalEnum.SINGLE ? moment(values.dueFromDate).toDate() : undefined)}
                          />
                        </div>
                      </section>
                      <section>
                        {!fullForm && <div className={classes.intervals}>
                          <IntervalSelect
                            name="interval"
                            allowCreate={false}
                            notAsync
                            label={lang.todo.form.interval}
                            value={values.interval}
                            onChange={(value) => {
                              if ( // reset responsible User if todo switches to series
                                values?.interval === TodoIntervalEnum.SINGLE
                                && value?.value !== TodoIntervalEnum.SINGLE
                              ) {
                                setFieldValue('responsibleUser', '')
                              }

                              setFieldValue('interval', value?.value)
                            }}
                            useFormik
                          />
                        </div>
                        }
                      </section>

                      {shouldRenderClientSelect && (
                        <section>
                          <div className={classes.collaborators}>
                            <CollaborationSelect
                              label={lang.todo.form.collaborators}
                              value={values.tenantCollaborationId}
                              onChange={value => {
                                const newValue = value?.value

                                setFieldValue('tenantCollaborationId', value)
                                setFieldValue('responsibleUser', '')

                                if (newValue !== TODO_COLLABORATION_ID.INTERNAL && values.tenantCollaborationId?.value !== newValue) {
                                  setFilesToUpload((currentFiles) => currentFiles.filter(fileEntry => fileEntry.source !== 'container'))
                                }
                              }}
                            />
                          </div>
                          <div className={classes.tooltipContainer}>
                            {!hasClientActiveSubscription && (
                              <RichTooltip body={lang.todo.readOnlyModeTooltipBody} width='500px' placement={'top'}>
                                <p className={classes.tooltipText}>{lang.todo.readOnlyModeTooltipText} <SVGIcon width={12} height={12} svg={IconInfo} /></p>
                              </RichTooltip>
                            )}
                          </div>
                        </section>
                      )}

                      <section className={classes}>
                        <UserSelect
                          hasClientModuleAccess={hasClientModuleAccess}
                          tenantCollaborationId={tenantCollaborationId}
                          label={lang.todo.form.responsible}
                          value={values.responsibleUser}
                          onChange={value => setFieldValue('responsibleUser', value)}
                          errorMessage={touched?.responsibleUser ? errors?.responsibleUser?.value : undefined}
                          customSelectOptions={{ useDefault: true }}
                          setResponsibleUserCount={setResponsibleUserCount}
                          fileId={filesToUpload.find(f => f.source === 'container')?.file?.id}
                        />
                      </section>

                      {(hasSupplierModuleAccess || hasCustomerModuleAccess) && (
                        <section className={classes.collapseRow}>
                          {hasCustomerModuleAccess && (
                            <div className={classes.customers}>
                              <CustomerSelect
                                label={lang.todo.form.customers}
                                value={values.customers}
                                onChange={value => setFieldValue('customers', value)}
                                isMulti
                                errorMessage={errors.customers}
                              />
                            </div>
                          )}
                          {hasSupplierModuleAccess && (
                            <div className={classes.suppliers}>
                              <SupplierSelect
                                label={lang.todo.form.suppliers}
                                value={values.suppliers}
                                onChange={value => setFieldValue('suppliers', value)}
                                isMulti
                                errorMessage={errors.suppliers}
                              />
                            </div>
                          )}
                        </section>
                      )}

                      {(!isEdit && values.type !== TodoTypes.SIGNATURE) && (
                        <section className={classes.documents}>
                          <div className={classes.documentLabel}>{lang.officeSubmenuDocumentsTitle}</div>
                          {filesToUpload.length > 0 ? fileList : null}
                        </section>
                      )}
                      {!document && !isEdit && (
                        <TodoFormFileUpload
                          tenantCollaborationId={tenantCollaborationId}
                          type={values.type}
                          hasFile={!!filesToUpload.length}
                          uploader={uploader}
                          onSelect={onSelectFromFileContainer}
                          onPreview={onPreviewFile} />
                      )}
                    </div>
                  </PerfectScrollbar>
                  <div className={classes.footer}>
                    <div className={classes.buttons}>
                      <ButtonIconSquare
                        label={lang.todo[`${isEdit ? 'edit' : 'create'}Todo`]}
                        icon={isEdit ? IconCheck : IconPlus}
                        type="submit"
                        disabled={isSubmitting || !hasActiveSubscription || (tenantCollaborationId && !hasClientActiveSubscription)}
                      />
                    </div>
                  </div>
                </Form>
              </div>
            </div>
          )
        }}
      </Formik>
    </div>
  )
}

TodoForm.propTypes = {
  inline: PropTypes.bool,
  collapse: PropTypes.bool,
  withTypeSelect: PropTypes.bool,
  withClientSelect: PropTypes.bool,
  tenantCollaborationId: PropTypes.string,
  tenantCollaborationName: PropTypes.string,
}

export default TodoForm
